import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreateInterviewInvite,
  useListInterviewTypes,
  getListInterviewInvitesQueryKey,
  getListCandidatesQueryKey,
  getGetCandidateQueryKey,
  type InterviewInviteResponseDto,
} from '@sara/api-client';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@sara/ui/components/dialog';
import type { DialogBaseProps } from '@sara/ui/components/dialog-provider';

import {
  CreateInterviewForm,
  type CreateInterviewFormValues,
} from '../../components/CreateInterviewForm';

/** Pull a human-readable message out of the api-client's thrown ApiError. */
function getApiErrorMessage(error: unknown): string {
  const data = (error as { data?: { message?: string | string[] } })?.data;
  const message = data?.message;
  if (Array.isArray(message)) return message.join(', ');
  if (typeof message === 'string' && message.trim()) return message;
  return 'Something went wrong. Please try again.';
}
import { normalizeUsPhone } from '../../components/CreateInterviewForm/schemas';
import { CreateInterviewSuccess } from '../../components/CreateInterviewSuccess';
import { CreateInterviewCalling } from '../../components/CreateInterviewCalling';

interface CreateInterviewDialogProps extends DialogBaseProps {
  orgId: string;
  lockedCandidate?: { id: string; firstName: string; lastName: string; email: string };
}

function CreateInterviewDialog({ orgId, onClose, lockedCandidate }: CreateInterviewDialogProps) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<InterviewInviteResponseDto | null>(null);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: allInterviewTypes = [] } = useListInterviewTypes({ orgId });
  const interviewTypes = allInterviewTypes.filter((type) => type.archivedAt === null);

  const { mutate, isPending } = useCreateInterviewInvite({
    mutation: {
      onSuccess: (data) => {
        void queryClient.invalidateQueries({ queryKey: getListInterviewInvitesQueryKey() });
        void queryClient.invalidateQueries({ queryKey: getListCandidatesQueryKey() });
        if (lockedCandidate?.id) {
          void queryClient.invalidateQueries({
            queryKey: getGetCandidateQueryKey(lockedCandidate.id),
          });
        }
        setResult(data);
      },
      onError: (error) => setErrorMessage(getApiErrorMessage(error)),
    },
  });

  function handleSubmit(values: CreateInterviewFormValues) {
    setErrorMessage(null);
    const { externalUserId, externalSource, channel, phoneNumber, dynamicVariables, ...rest } =
      values;
    // Drop empty entries — the server applies declared defaults at conversation start.
    const cleanedVariables = Object.fromEntries(
      Object.entries(dynamicVariables ?? {}).filter(([, v]) => v.trim() !== '')
    );
    // Normalize the US number to E.164 (+1XXXXXXXXXX) for the API. Validation
    // guarantees a valid number when channel is "call", so this is non-null there.
    const normalizedPhone = normalizeUsPhone(phoneNumber);
    mutate({
      data: {
        orgId,
        ...rest,
        ...(externalUserId && externalSource ? { externalUserId, externalSource } : {}),
        ...(channel === 'call' && normalizedPhone
          ? { delivery: { channel: 'call', phoneNumber: normalizedPhone } }
          : {}),
        ...(Object.keys(cleanedVariables).length ? { dynamicVariables: cleanedVariables } : {}),
      },
    });
  }

  // A call was placed as part of creation — show the dialing/queued state.
  if (result?.call) {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            Calling now
          </DialogTitle>
          <DialogDescription>
            The invite was created and we&apos;re placing the call.
          </DialogDescription>
        </DialogHeader>
        <CreateInterviewCalling
          candidateName={`${result.candidate.firstName} ${result.candidate.lastName}`}
          interviewInviteId={result.interviewInviteId}
          onClose={onClose}
        />
      </DialogContent>
    );
  }

  if (result) {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            Interview created
          </DialogTitle>
          <DialogDescription>The invite has been generated successfully.</DialogDescription>
        </DialogHeader>
        <CreateInterviewSuccess
          candidateName={`${result.candidate.firstName} ${result.candidate.lastName}`}
          interviewUrl={result.url}
          interviewInviteId={result.interviewInviteId}
          onClose={onClose}
        />
      </DialogContent>
    );
  }

  return (
    <DialogContent className="max-h-[85dvh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium tracking-tight">
          New interview
        </DialogTitle>
        <DialogDescription>
          Create an invite and share the link with the candidate.
        </DialogDescription>
      </DialogHeader>
      {errorMessage && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}
      <CreateInterviewForm
        interviewTypes={interviewTypes}
        onSubmit={handleSubmit}
        onCancel={onClose}
        isPending={isPending}
        lockedCandidate={lockedCandidate}
      />
    </DialogContent>
  );
}

export { CreateInterviewDialog };
